import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { Tooltip } from "../../utils/local-antd";
import type { CollapseProps } from "../../utils/local-antd";

import React, { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useTheme } from "../../ThemeContext";
import { AssigneeBanner } from "../AssigneeBanner";
import { AtiraCollapse } from "../AtiraCollapse";
import { AtiraEmpty } from "../AtiraEmpty";
import { AtiraIcon } from "../AtiraIcon";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { Text } from "../Text";

import { permissionSliceSelectors } from "../../redux/permission/permission.selector";
import { useAppSelector } from "../../redux/store";
import { Spacing } from "../../theme/Spacing";
import { TaskDetails } from "./TaskDetails";
import { TextWithTruncated } from "../TextWithTruncated";
import {
  getTaskDeadlineColor,
  getTaskDeadlineDateAsText,
  getTaskPriorityColor,
} from "../../utils/taskUtils";
import capitalize from "lodash/capitalize";
import { ContactTask } from "../../model/task/Task";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { useDrawersContext } from "../../routes/tasks/drawers-context";
import { EntryKind } from "../../model/entry/types/EntryKind.enum";
import { entrySliceSelectors } from "../../redux/entry/entry.selector";

const PriorityButton = styled(Button)`
  background-color: ${({ theme }) => theme.transparent};
  margin: 0;
  padding: 0;
`;

const EmptyWrapper = styled(Flex)`
  margin-top: ${Spacing.l};

  .ant-empty-image {
    height: 4rem;
  }
`;

type TasksReadTabProps = {
  kind: TaskKind;
};

export const TasksReadTab: React.FC<TasksReadTabProps> = ({ kind }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const {
    setTaskCreateVisible,
    setTaskKind,
    entry: _entry,
    kanbanCard,
  } = useDrawersContext();
  const entry =
    useAppSelector((s) =>
      entrySliceSelectors.selectEntryById(s, _entry?._id || ""),
    ) || _entry;

  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const cardFinalized = Boolean(kanbanCard && Boolean(kanbanCard?.closedAt));

  const getEmptyAction = () => {
    if (cardFinalized) {
      return undefined;
    }

    if (kind === TaskKind.CONTACT && entry?.kind === EntryKind.CONTACT) {
      return {
        title: t(`common.createContactTask`),
        onClick: () => {
          setTaskKind(TaskKind.CONTACT);
          setTaskCreateVisible(true);
        },
      };
    }

    if (kind === TaskKind.LEAD && entry?.kind === EntryKind.LEAD) {
      return {
        title: t(`common.createLeadTask`),
        onClick: () => {
          setTaskKind(TaskKind.LEAD);
          setTaskCreateVisible(true);
        },
      };
    }

    if (kind === TaskKind.KANBAN && kanbanCard) {
      return {
        title: t(`common.createKanbanTask`),
        onClick: () => {
          setTaskKind(TaskKind.KANBAN);
          setTaskCreateVisible(true);
        },
      };
    }

    return undefined;
  };

  const items: CollapseProps["items"] = (() => {
    const panelStyle: CSSProperties = {
      borderBottom: "1px solid black",
      backgroundColor: theme.sub,
      border: "0",
    };

    const getTasksByParentKind = () => {
      switch (kind) {
        case TaskKind.CONTACT:
          return entry?.contactTasks;
        case TaskKind.LEAD:
          return entry?.leadTasks;
        case TaskKind.KANBAN:
          return kanbanCard?.kanbanTasks;
        default:
          return [];
      }
    };

    const tasks = getTasksByParentKind();

    if (tasks?.length) {
      return tasks.map((task) => {
        const isContactTask = task.kind === TaskKind.CONTACT;

        return {
          key: task._id,
          label: (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              key={task._id}
            >
              <Flex alignItems="center" gap="m">
                <Tooltip
                  title={
                    isContactTask
                      ? t(
                          `common.contactTask${capitalize((task as ContactTask)?.priority)}Priority`,
                          {
                            priority: t(
                              `common.${(task as ContactTask)?.priority?.toLowerCase()}`,
                            ),
                          },
                        )
                      : undefined
                  }
                >
                  <Flex alignItems="center" gap="s">
                    {isContactTask ? (
                      <PriorityButton
                        icon={faCircle}
                        iconWidth="sm"
                        color={getTaskPriorityColor(
                          (task as ContactTask)?.priority,
                        )}
                      />
                    ) : null}
                    <TextWithTruncated
                      length={20}
                      color={
                        isContactTask
                          ? getTaskPriorityColor(
                              (task as ContactTask)?.priority,
                            )
                          : "black"
                      }
                    >
                      {task?.name}
                    </TextWithTruncated>
                  </Flex>
                </Tooltip>

                <AssigneeBanner
                  assignees={members?.filter((m) =>
                    task.assigneeIds?.includes(m._id),
                  )}
                />
              </Flex>
              <Flex alignItems="center">
                {task.done === true ? (
                  <Flex alignItems="center" gap="s">
                    <AtiraIcon icon={faCheck} size="lg" color="success" />
                    <Text color="success">{t("common.done")}</Text>
                  </Flex>
                ) : null}

                {!task?.done && task?.deadline ? (
                  <Button
                    margin="none"
                    style={{
                      backgroundColor: theme[getTaskDeadlineColor(task)],
                    }}
                  >
                    {getTaskDeadlineDateAsText(new Date(task.deadline), t)}
                  </Button>
                ) : null}
              </Flex>
            </Flex>
          ),
          children: task ? (
            <TaskDetails
              task={task}
              key={task._id}
              hideActions={cardFinalized}
            />
          ) : null,
          style: panelStyle,
          destroyInactivePanel: true,
        };
      }) as CollapseProps["items"];
    }

    return [];
  })();

  if (!entry && !kanbanCard) {
    return null;
  }

  return items?.length ? (
    <AtiraCollapse items={items} accordion />
  ) : (
    <EmptyWrapper>
      <AtiraEmpty
        description={
          kanbanCard
            ? t("kanban.tasks.empty")
            : t("entries.contact_tasks.empty")
        }
        action={getEmptyAction()}
      />
    </EmptyWrapper>
  );
};
